import React from "react";
import { DialogLayout } from "../ui/layouts/DialogLayout";
import { Button } from "../ui/components/Button";
import { Badge } from "../ui/components/Badge";
import { IconWithBackground } from "../ui/components/IconWithBackground";
import { FeatherRepeat } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { FeatherEdit2 } from "@subframe/core";
import { FeatherArrowRight } from "@subframe/core";
import { FeatherCheck } from "@subframe/core";
import { FeatherInfo } from "@subframe/core";

export interface TemplateChange {
  field: string;
  oldValue: string;
  newValue: string;
  label: string;
}

interface ConfirmEditRecurringJournalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changes: TemplateChange[];
  onConfirm: () => void;
  loading?: boolean;
}

const ConfirmEditRecurringJournalModal: React.FC<
  ConfirmEditRecurringJournalModalProps
> = ({ open, onOpenChange, changes, onConfirm, loading = false }) => {
  const handleConfirm = () => {
    onConfirm();
  };

  const handleClose = () => {
    if (!loading) {
      onOpenChange(false);
    }
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-192 flex-none flex-col items-start gap-6 rounded-lg border border-solid border-neutral-border bg-default-background px-8 py-6 shadow-lg">
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full items-start gap-3">
            <IconWithBackground
              variant="warning"
              size="large"
              icon={<FeatherRepeat />}
            />
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Update Recurring Journal Template
              </span>
              <span className="text-body font-body text-subtext-color">
                Review the changes you're about to make to this recurring
                journal template.
              </span>
            </div>
            <FeatherX
              className="text-heading-3 font-heading-3 text-default-font cursor-pointer"
              onClick={handleClose}
            />
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
          <div className="flex items-center gap-2">
            <FeatherEdit2 className="text-body font-body text-subtext-color" />
            <span className="text-heading-3 font-heading-3 text-default-font">
              Changes Summary
            </span>
          </div>
          <div className="flex w-full flex-col items-start gap-3">
            {changes.map((change, index) => (
              <div
                key={index}
                className="flex w-full items-start gap-4 rounded-md border border-solid border-neutral-200 bg-white px-4 py-4"
              >
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="neutral">{change.label}</Badge>
                    <span className="text-caption font-caption text-subtext-color">
                      Modified
                    </span>
                  </div>
                  {change.field === "notes" ||
                  change.field === "template_name" ? (
                    <div className="flex w-full flex-col items-start gap-2">
                      <span className="text-body font-body text-neutral-400 line-through">
                        {change.oldValue}
                      </span>
                      <div className="flex items-center gap-2">
                        <FeatherArrowRight className="text-caption font-caption text-subtext-color" />
                        <span className="text-body-bold font-body-bold text-default-font">
                          {change.newValue}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex w-full items-center gap-2">
                      <span className="text-body font-body text-neutral-400 line-through">
                        {change.oldValue}
                      </span>
                      <FeatherArrowRight className="text-caption font-caption text-subtext-color" />
                      <span className="text-body-bold font-body-bold text-default-font">
                        {change.newValue}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex w-full items-start gap-3 rounded-md border border-solid border-warning-600 bg-warning-50 px-6 py-4">
          <FeatherInfo className="text-heading-3 font-heading-3 text-warning-600" />
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <span className="text-body-bold font-body-bold text-default-font">
              These changes will apply to all future journal entries
            </span>
            <span className="text-body font-body text-subtext-color">
              This update will affect all subsequent recurring journal entries
              generated from this template.
            </span>
          </div>
        </div>
        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
        <div className="flex w-full items-center justify-between">
          <Button
            variant="neutral-tertiary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            icon={<FeatherCheck />}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? "Updating..." : "Confirm Update"}
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default ConfirmEditRecurringJournalModal;
